// Reveal animations from Webflow IX2:
//  - SlideIn: the site-wide "slide in from bottom" preset on every .padding-global section
//    (opacity 0 -> 1 and 100px -> 0 over 1s, outQuart, 500ms after it scrolls into view). The
//    animation itself is an inline script in index.html, so it starts before the app loads.
//  - HeroGlow: the home page "Hero Glow [Page Loaded]" fade-up.

import { useEffect, useRef, useState } from 'react';
import type { WfProps } from '../webflow/element';
import { css } from './easing';

declare global {
  interface Window {
    /** Starts an element's slide-in reveal (defined by an inline script in index.html). */
    wfSlideIn?: (el: HTMLElement) => void;
  }
}

export function SlideIn({ as: Tag = 'div', children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  // Prerendered sections were started by index.html's script before the app loaded; this
  // covers sections React renders itself (`vite dev`, or content rendered after load).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.wfSlideIn) window.wfSlideIn(el);
    else el.setAttribute('data-ix-shown', '');
  }, []);
  return (
    <Tag ref={ref} data-ix="slide-in" {...props}>
      {children}
    </Tag>
  );
}

export function HeroGlow({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el?.animate) {
      setDone(true);
      return undefined;
    }
    const move = el.animate([{ transform: 'translate3d(0, 5rem, 0)' }, { transform: 'translate3d(0, 0, 0)' }], {
      duration: 1000,
      delay: 500,
      easing: css('outQuad'),
      fill: 'both',
    });
    const fade = el.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 1500,
      delay: 500,
      easing: css('outQuad'),
      fill: 'both',
    });
    let cancelled = false;
    void Promise.all([move.finished, fade.finished])
      .then(() => !cancelled && setDone(true))
      .catch(() => undefined);
    return () => {
      cancelled = true;
      move.cancel();
      fade.cancel();
    };
  }, []);
  // Server render keeps Webflow's initial state (shifted down, transparent).
  const finalStyle = {
    ...style,
    WebkitTransform: undefined,
    MozTransform: undefined,
    msTransform: undefined,
    transform: 'translate3d(0px, 0px, 0px)',
    opacity: 1,
  };
  return (
    <Tag ref={ref} {...props} style={done ? finalStyle : style}>
      {children}
    </Tag>
  );
}
